// utils/degreePlanUtils.ts
import type Program from '@/models/program';
import type School from '@/models/school';


function isElcr(code: any) {
  const s = String(code ?? '').trim().toUpperCase();
  return /^ELCR\b/.test(s);
}

function isMncr(code: any) {
  const s = String(code ?? '').trim().toUpperCase();
  return /^MNCR\b/.test(s);
}


// Normalize a requirement name for display: strip leading "N. " numbering,
// strip code-like parentheticals (e.g. " (R2715/L30)"), and if the result is
// still in raw underscore form, replace underscores with spaces and capitalize
// only the first letter.
function humanizeRequirementName(name: string): string {
  if (!name) return name;
  let cleaned = String(name)
    .replace(/^\s*\d+\.\s*/, '')
    .replace(/\s*\([A-Z0-9][A-Z0-9/\-_\s]*\)/g, '');

  if (cleaned.includes('_')) {
    const lower = cleaned.replace(/_/g, ' ').trim().toLowerCase();
    cleaned = lower.length === 0
      ? lower
      : lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  return cleaned.replace(/\s+/g, ' ').trim();
}

const AGEC_SHORT_DISPLAY_NAMES: Record<string, string> = {
  'Biological & Physical Sciences': 'Science Course',
  'Social & Behavioral Sciences': 'Social Science Course',
};

// Derive a display title from a Pima AGEC start_req, e.g.
// "AGEC B Fine Arts Minimum Credits" → "Fine Arts Course".
function agecElectiveDisplayName(course: any): string | null {
  const toList = (v: any): string[] => {
    if (Array.isArray(v)) return v.filter((x) => x != null && String(x).trim() !== '');
    if (typeof v === 'string' && v.trim() !== '') return [v];
    return [];
  };

  for (const raw of toList(course?.start_req)) {
    const name = humanizeRequirementName(String(raw));
    const match = name.match(/^AGEC\s+[A-Z]\s+(.+?)\s+(?:Option\s+)?Minimum Credits$/i);
    if (!match) continue;
    const subject = match[1].trim();
    return AGEC_SHORT_DISPLAY_NAMES[subject] ?? `${subject} Course`;
  }
  return null;
}

function resolveAgecElectiveDisplayName(course: any): string | null {
  const fromReq = agecElectiveDisplayName(course);
  if (fromReq) return fromReq;
  const name = String(course?.name ?? '').trim();
  if (name && name !== 'Free Elective') return name;
  return null;
}

// Bullet list of the requirements (Pima + UA) the course satisfies. Newlines
// render as line breaks via `white-space: pre-line` on `.course-description`.
function formatSatisfiedRequirements(
  c: any,
  sourceSchoolName: string,
  destinationSchoolName: string
): string {
  const toList = (v: any): string[] => {
    if (Array.isArray(v)) {
      return v.filter(
        (x) => x != null && String(x).trim() !== ''
      );
    }

    if (typeof v === 'string' && v.trim() !== '') {
      return [v];
    }

    return [];
  };

  const startReq = toList(c?.start_req).map(humanizeRequirementName);
  const destReq = toList(c?.dest_req).map(humanizeRequirementName);

  if (startReq.length === 0 && destReq.length === 0) {
    return '';
  }

  const sections: string[] = [];

  if (destReq.length > 0) {
    sections.push(
      `At the ${destinationSchoolName}:\n` +
      destReq.map((r) => ` • ${r}`).join('\n')
    );
  }

  if (startReq.length > 0) {
    sections.push(
      `At ${sourceSchoolName}:\n` +
      startReq.map((r) => ` • ${r}`).join('\n')
    );
  }

  return sections.join('\n\n');
}

function descriptionForCourse(
  c: any,
  display: string,
  sourceSchoolName: string,
  destinationSchoolName: string
) {
  if (isMncr(c?.code)) {
    return 'Placeholder for minor program courses. Work with your advisor to choose specific minor courses.';
  }

  const courseName = c?.name || display;

  const reqList = formatSatisfiedRequirements(
    c,
    sourceSchoolName,
    destinationSchoolName
  );

  if (isElcr(c?.code)) {
    if (!reqList) {
      return 'Any course can be used to satisfy this elective.';
    }

    return `This elective should satisfy the following degree requirements:\n\n${reqList}`;
  }

  if (!reqList) {
    return `${courseName} does not satisfy a specific listed degree requirement.`;
  }

  return `${courseName} satisfies the following degree requirements:\n\n${reqList}`;
}

// Collapse multi-bucket `course_options` to a single entry containing the
// intersection (by course id) so the modal renders one combined section. An
// empty intersection means the current course is the only valid swap, in
// which case we drop the section entirely.
function buildIntersectedCourseOptions(
  courseOptions: Record<string, any[]> | null | undefined
): Record<string, any[]> {
  if (!courseOptions) return {};
  const entries = Object.entries(courseOptions).filter(([, v]) => Array.isArray(v));
  if (entries.length === 0) return {};
  if (entries.length === 1) {
    const [name, list] = entries[0];
    return list.length === 0 ? {} : { [humanizeRequirementName(name)]: list };
  }

  const [, first] = entries[0];
  const intersection = first.filter((cand: any) =>
    entries.slice(1).every(([, list]) =>
      list.some((other: any) => other?.id === cand?.id)
    )
  );
  if (intersection.length === 0) return {};
  const combinedName = entries
    .map(([name]) => humanizeRequirementName(name))
    .join(' and ');
  return { [combinedName]: intersection };
}

export function createTermObjects(
  courseList: Array<any>,
  startTermFrom: number,
opts?: {
  includeTransferCourses?: boolean;
  takenCourses?: any[];
  originalSchool?: string;
  includeSourceRequirement?: boolean;
  sourceSchoolName?: string;
  destinationSchoolName?: string;
}
) {
const includeTransferCourses = !!opts?.includeTransferCourses;
const takenCourses = opts?.takenCourses ?? [];
const originalSchool = opts?.originalSchool ?? 'Transfer Credit';
const includeSourceRequirement = !!opts?.includeSourceRequirement;
const sourceSchoolName =
  opts?.sourceSchoolName ?? 'Pima Community College';

const destinationSchoolName =
  opts?.destinationSchoolName ?? 'University of Arizona';

const getSourceRequirement = (course: any) => {
  const req =
    course?.sourceRequirement ??
    course?.source_requirement ??
    course?.pimaRequirement ??
    course?.pima_requirement ??
    course?.requirement ??
    '';

  if (Array.isArray(req)) return req.join(' ');
  return String(req || '');
};

  const terms: Array<any> = [];
  const transferredCourseCodes = new Set<string>();

  // ---- Transfer/Prior term (position 0) ----
  if (includeTransferCourses && Array.isArray(takenCourses) && takenCourses.length > 0) {
    const transferTerm = {
      id: 0,
      name: 'UA Transfer Equivalents',
      credits: 0,
      position: 0,
      plan_items: [] as any[],
      // support both snake_case and camelCase for downstream components
      is_transfer: true,
      had_transfer: true,
      isTransfer: true,
      hadTransfer: true,
      locked: true,
    };

    takenCourses.forEach((course: any, index: number) => {
      const sourceCourseName = course.full_name || course.name || '';
      const sourceCode = course.code || '';
      const sourceCredit = parseInt(course.minimum_credits || course.credits || '0', 10) || 0;

      // your API field might be 'equivalency' or 'ua_equivalency'
      const eqRaw = course.equivalency ?? course.ua_equivalency ?? '';
      const hasEquivalency =
        !!eqRaw && !/^\s*(no (equivalen(cy|t))|none)\s*$/i.test(eqRaw);
      if (hasEquivalency) {
        eqRaw.split(',').forEach((eq: string) => {
          const code = (eq || '').trim().toUpperCase();
          if (code) transferredCourseCodes.add(code);
        });
      }

const sourceRequirement = getSourceRequirement(course);
const planItem = {
  id: `transfer-${course.id ?? index}`,
  name: hasEquivalency ? eqRaw : 'No UofA Equivalent',
  credits: hasEquivalency ? sourceCredit : 0,
  sourceRequirement,
  source_requirement: sourceRequirement,
        custom: false,
        position: index,
        plan_item_spec: null,
        locked: true,
        // flags for item-level transfer too
        is_transfer: true,
        isTransfer: true,
        transfer_info: {
          source_requirement: sourceRequirement,
          original_school: originalSchool,
          original_course: sourceCourseName,
          original_code: sourceCode,
          ua_equivalent: hasEquivalency ? eqRaw : null,
          has_equivalency: hasEquivalency,
        },
        requirement: {
          id: `transfer-req-${course.id ?? index}`,
          name: hasEquivalency ? eqRaw : 'No UofA Equivalent',
          parent_id: 0,
          type: 'TransferCourseRequirement',
          credits: hasEquivalency ? sourceCredit : 0,
          sourceRequirement,
          source_requirement: sourceRequirement,
          advanced: false,
          attempts: 1,
          minimum_grade: hasEquivalency ? 'C' : 'N/A',
            course: {
              id: course.id ?? index,
              code: hasEquivalency ? eqRaw.split(',')[0].trim() : sourceCode,
              full_number: hasEquivalency ? eqRaw.split(',')[0].trim() : sourceCode,
              name: hasEquivalency ? eqRaw : 'No Equivalent',
              full_name: hasEquivalency ? eqRaw : sourceCourseName,
              minimum_credits: hasEquivalency ? sourceCredit : 0,
              maximum_credits: hasEquivalency ? sourceCredit : 0,
              is_transfer: true,
              has_equivalency: hasEquivalency,
              description: hasEquivalency
                ? `Transfer credit from ${originalSchool}\nOriginal Course: ${sourceCourseName}\nUA Equivalent: ${eqRaw}`
                : `Transfer credit from ${originalSchool}\nOriginal Course: ${sourceCourseName}\nNo UA Equivalent Found`,
              academic_period: {
                id: 0,
                term: 'Transfer',
                year: new Date().getFullYear(),
                code: 'TRANSFER',
                name: 'Transfer Credit',
                academic_year: 'Transfer',
              },
            },
          },
        };

      transferTerm.plan_items.push(planItem);
      if (hasEquivalency) transferTerm.credits += planItem.credits;
    });

    terms.push(transferTerm);
  }

  // ---- Future terms (filter out courses already satisfied by transfer) ----
  const futureCourses = (courseList || []).filter(c => {
    if (c?.is_transfer) return false;
    const code = (c?.code || '').toUpperCase().trim();
    return !transferredCourseCodes.has(code);
  });

  if (futureCourses.length) {
    const maxTerm = Math.max(...futureCourses.map(c => c.term));
    const minTerm = Math.min(...futureCourses.map(c => c.term));
    for (let i = minTerm; i <= maxTerm; i++) {
      const actualTermNumber = i - minTerm + 1;
      terms.push({
        id: i,
        name: `Term ${actualTermNumber}`,
        credits: 0,
        position: (includeTransferCourses ? 1 : 0) + (i - minTerm),
        plan_items: [] as any[],
        locked: false,
      });
    }


    futureCourses.forEach(course => {
      const term = terms.find(t => t.id === course.term);
      if (!term) return;

      const minCred = parseInt(course.minimum_credits, 10) || 0;
      const maxCred = parseInt(course.maximum_credits ?? course.minimum_credits, 10) || minCred;
      const sourceRequirement = includeSourceRequirement ? getSourceRequirement(course) : '';
      const destinationEquivalency =
        course?.destination_course ??
        course?.equivalency ??
        course?.ua_equivalency ??
        course?.ua_equivalent ??
        null;
      const elcr = isElcr(course.code);
      const mncr = isMncr(course.code);
      const agecName = elcr ? resolveAgecElectiveDisplayName(course) : null;
      const topLine = agecName ?? (elcr ? 'Free Elective' : mncr ? 'Minor Course' : (course.code || ''));
      const secondLine = agecName ?? (
        elcr
          ? 'Any transferable course'
          : mncr
            ? 'Minor program requirement'
            : (course.full_name || course.name || course.code || 'Untitled Course')
      );

const planItem = {
  id: course.id,
  name: secondLine,
  credits: minCred,
  sourceRequirement,
  source_requirement: sourceRequirement,
        custom: false,
        position: term.plan_items.length,
        plan_item_spec: null,
        course_options: buildIntersectedCourseOptions(course.course_options),
        locked: false,
        requirement: {
          id: course.name,
          name: secondLine,
          parent_id: term.id,
          type: 'CourseRequirement',
          credits: minCred,
          sourceRequirement,
          source_requirement: sourceRequirement,
          advanced: false,
          attempts: 1,
          minimum_grade: course.minimum_grade,
          course: {
            id: course.id,
            code: topLine,
            full_number: topLine,
            name: secondLine,
            full_name: secondLine,
            minimum_credits: minCred,
            maximum_credits: maxCred,

            // Preserve requirement mappings used in course details.
            start_req: course.start_req,
            dest_req: course.dest_req,

            // Preserve every form of the destination equivalency.
            equivalency: course.equivalency,
            destination_course: destinationEquivalency,
            ua_equivalency: course.ua_equivalency,
            ua_equivalent: destinationEquivalency,

            description: descriptionForCourse(
              course,
              secondLine,
              sourceSchoolName,
              destinationSchoolName
            ),

            catalog_description:
              course.catalog_description ||
              course.course_description ||
              '',

            course_description:
              course.catalog_description ||
              course.course_description ||
              '',

            academic_period: {
              id: term.id,
              term: term.name,
              year: new Date().getFullYear(),
              code: `${new Date().getFullYear()}.${term.id}`,
              name: term.name,
              academic_year: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
            },

            // Required by the grid and graph prerequisite visualizations.
            prereq: course.prereq,
            coreq: course.coreq,
            canonical_course: course.canonical_course ?? null,
            },
          },
        };

      term.plan_items.push(planItem);
      term.credits += planItem.credits;
    });
  }

  // Keep terms ordered by position for the visualization
  terms.sort((a, b) => a.position - b.position);
  return terms;
}

/**
 * Detects if a program requires a minor
 */
export function detectMinorRequirement(requirementsObj: any): boolean {
  if (!requirementsObj || typeof requirementsObj !== "object") {
    return false;
  }

  const looksMinor = (n: any) => {
    const id = String(n?.id ?? "");
    const name = String(n?.name ?? "");
    const notes = String(n?.notes ?? "");

    const isR513 = id.toUpperCase().startsWith("R513");
    const hasMinorInName = /minor/i.test(name) && /required/i.test(name);
    const hasMinorInNotes = /minor is required/i.test(notes);

    return isR513 || hasMinorInName || hasMinorInNotes;
  };

  const stack = [requirementsObj];
  while (stack.length) {
    const cur: any = stack.pop();
    if (cur && typeof cur === "object") {
      if (looksMinor(cur)) {
        return true;
      }

      const kids = ([] as any[]).concat(
        cur.rules ?? [],
        cur.subRules ?? [],
        cur.requisitesSimple ?? [],
        cur.children ?? [],
        cur.requisites?.requisitesSimple ?? []
      );

      for (const k of kids) {
        if (k) stack.push(k);
      }
    }
  }

  return false;
}

/**
 * Converts a program code to a minor code
 */
export function convertToMinorCode(programCode: string): string {
  const cleanCode = programCode.replace(/(?:BA|BS|MS|MA|PHD)$/i, '');
  return `${cleanCode}MINU`;
}

/**
 * Merges minor requirements into degree requirements
 */
export function mergeMinorIntoDegreeRequirements(degreeReqs: any, minorReqs: any): any {
  const merged = JSON.parse(JSON.stringify(degreeReqs));

  const findAndReplaceMinorRequirement = (obj: any) => {
    if (!obj || typeof obj !== 'object') return;

    if (obj.id && String(obj.id).toUpperCase().startsWith("R513")) {
      obj.name = `Minor: ${minorReqs.requisites?.requisitesSimple?.[0]?.name || 'Selected Minor'}`;
      obj.description = `Requirements for selected minor program`;
      obj.condition = "allOf";
      obj.subRules = minorReqs.requisites?.requisitesSimple || [];
      delete obj.value;
      return;
    }

    const searchProps = ['rules', 'subRules', 'requisitesSimple', 'children'];
    for (const prop of searchProps) {
      if (Array.isArray(obj[prop])) {
        obj[prop].forEach(findAndReplaceMinorRequirement);
      }
    }

    if (obj.requisites && typeof obj.requisites === 'object') {
      findAndReplaceMinorRequirement(obj.requisites);
    }
  };

  findAndReplaceMinorRequirement(merged);
  addMissingConditionFields(merged);

  return merged;
}

/**
 * Adds missing condition fields to requirement nodes
 */
function addMissingConditionFields(obj: any): void {
  if (!obj || typeof obj !== 'object') return;

  if (obj.id && !obj.condition) {
    if (obj.requirementLevel === "subplan" || obj.id.includes("NO_SUBPLANS")) {
      obj.condition = "freeformText";
      obj.value = "";
    } else if (obj.rules || obj.subRules) {
      obj.condition = "allOf";
    } else {
      obj.condition = "freeformText";
      obj.value = "";
    }
  }

  const searchProps = ['rules', 'subRules', 'requisitesSimple', 'children'];
  for (const prop of searchProps) {
    if (Array.isArray(obj[prop])) {
      obj[prop].forEach((item: any) => addMissingConditionFields(item));
    }
  }

  if (obj.requisites && typeof obj.requisites === 'object') {
    addMissingConditionFields(obj.requisites);
  }
}